/* eslint-disable no-trailing-spaces */
/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';
import { QueryOptions } from '../Utils/QueryOptions';
import InputFilter from './Components/InputFilter';
import SelectFilter from './Components/SelectFilter';
import AutocompleteFilter from './Components/AutocompleteFilter';
import InputRangeFilter from './Components/InputRangeFilter';
import { FilterTypes } from './Components';
import DatePickerFilter from './Components/DatePickerFilter';
import DateRangeFilter from './Components/DateRangeFilter';
import SelectedFilters from './SelectedFilters';
import DownIcon from '../Icons/DownIcon';
import SortRightIcon from '../Icons/SortRightIcon';
import { isUndefinedOrEmpty } from '../../utils/js';

/**
Il componente `Filter` visualizza una serie di input per impostare dei filtri di ricerca come ad esempio nel caso di una tabella. 
Esso riceve come proprietà la lista dei campi di filtro da mostrare e permette di specificare una callback da invocare quando viene aggiunto o rimosso un filtro; 
è inoltre possibile impostare dei filtri iniziali attraverso la prop `initialFilters`

*Attenzione:* Dalla versione del Framework 3.1 viene utilizzato lo standard OData 4 e non più lo standard OData 2.   
Il framework-ui-components gestisce di default lo standard OData 2.   
Per cambiare il comportamento di default, chiamare il metodo `useODataVersion4` il prima possibile.

```js
import { useODataVersion4 } from 'framework-ui-components/Components/Utils/QueryOptions';
useODataVersion4();
```

Esiste anche la funzione `useODataVersion2`, ma come detto non dovrebbe mai essere utilizzata in quando imposta la gestione di default.
 */
class Filter extends Component {
  constructor(props) {
    super(props);

    this.state = {
      filter: this.initializeFilter(props),
      showOtherFilters: false,
    };
    this.filterRefs = [];
  }

  componentDidMount() {
    const { initialFilters, filterCallback } = this.props;
    const { filter } = this.state;
    if (initialFilters && filterCallback) {
      this.onFilterAdded(filter, true);
    }
  }

  initializeFilter = (props) => {
    const initFilters = {};

    props.initialFilters && props.initialFilters.forEach((x) => {
      if (!Object.keys(initFilters).find((y) => y === x.propertyName)) {
        initFilters[x.propertyName] = [];
      }

      const filtroRic = props.filtriRicerca.find((y) => y.propertyName === x.propertyName);
      const filtroRicDescription = filtroRic.description ? filtroRic.description : filtroRic.propertyName;
      initFilters[x.propertyName].push({
        description: x.description ? x.description : filtroRicDescription,
        displayValue: x.displayValue,
        name: x.propertyName,
        query: x.query,
        valore: x.value,
        filterType: x.filterType,
        customFilter: x.customFilter,
      });
    });

    return initFilters;
  };

  checkValidity = (item, queryOption, evt, autocompleteObj, range, filterType) => {
    if (!item) {
      return false;
    }

    if (autocompleteObj != null && autocompleteObj.result !== '') {
      return true;
    }
    if (evt && filterType === FilterTypes.SELECT) {
      return true;
    }
    if (evt && (evt.keyCode === 13 || filterType === FilterTypes.SELECT
      || filterType === FilterTypes.DATEPICKER)/* && filterValue !== "" */) {
      return true;
    }
    if (filterType === FilterTypes.DATERANGE && range) {
      return true;
    }
    return false;
  };

  collectValues = (item, queryOption, evt, autocompleteObj, range, filterType, customFilter) => {
    const filterObj = {
      name: item,
      query: queryOption,
      customFilter,
      filterType,
    };
    const { filtriRicerca } = this.props;

    const desc = filtriRicerca
      .find((x) => x.propertyName === item
        && (range != null || (range == null && x.queryOption === queryOption))).description;

    switch (filterType) {
      case FilterTypes.INPUT:
        filterObj.valore = evt.target.value;
        filterObj.description = desc;
        filterObj.displayValue = evt.target.displayValue;
        break;

      case FilterTypes.SELECT:
        filterObj.valore = evt.target.value;
        filterObj.description = desc;
        filterObj.displayValue = evt.target.displayValue;
        break;

      case FilterTypes.AUTOCOMPLETE:
        filterObj.valore = autocompleteObj.result.Value;
        filterObj.description = desc;
        filterObj.displayValue = autocompleteObj.result[autocompleteObj.props.item.renderProperty || 'Description'];
        break;

      case FilterTypes.RANGE:
        filterObj.valore = range.values;
        filterObj.description = desc;
        filterObj.displayValue = evt.target.displayValue;
        break;

      case FilterTypes.DATEPICKER:
        filterObj.valore = evt.target.value;
        filterObj.description = desc;
        filterObj.displayValue = evt.target.displayValue;
        break;

      case FilterTypes.DATERANGE:
        filterObj.valore = range.values;
        filterObj.description = desc;
        filterObj.displayValue = range.displayValue;
        break;

      default:
        break;
    }

    const { filter } = this.state;
    // controllo se è già impostato un filtro con quel valore
    let index = -1;
    for (let i = 0; i < filter[item].length; i += 1) {
      if (filter[item][i].valore === filterObj.valore) {
        index = i;
      }
    }
    // se già esiste non aggiungo il nuovo filtro
    if (index !== -1) {
      return null;
    }

    return filterObj;
  };

  clearFields = (type, evt, range, item) => {
    if (range && range.clearFields) {
      range.clearFields();
    } else if (evt != null) {
      // Sbianco i filtri per input
      if (this.filterRefs[item]) {
        this.filterRefs[item].clear();
      }
    }
  };

  addFilter = (item, queryOption, evt, autocompleteRef, rangeRef, filterType, customFilter) => {
    if (this.checkValidity(item, queryOption, evt, autocompleteRef, rangeRef, filterType)) {
      if (evt && filterType === FilterTypes.INPUT) {
        evt.persist();
      }

      const { filter } = this.state;
      if (filter[item] == null) {
        filter[item] = [];
      }

      const newFilter = this.collectValues(
        item,
        queryOption,
        evt,
        autocompleteRef,
        rangeRef,
        filterType,
        customFilter,
      );
      if (!newFilter || isUndefinedOrEmpty(newFilter.valore)) {
        return;
      }

      filter[item] = [...filter[item], newFilter];

      this.setState({ filter }, () => {
        this.onFilterAdded(filter, false);
        this.clearFields(filterType, evt, rangeRef, item);
      });
    }
  };

  removeFilter = (item) => {
    const { filter } = this.state;
    let index = -1;
    for (let i = 0; i < filter[item.name].length; i += 1) {
      if (filter[item.name][i].valore === item.valore) {
        index = i;
      }
    }

    filter[item.name].splice(index, 1);
    if (filter[item.name].length === 0) {
      delete filter[item.name];
    }

    this.setState({
      filter,
    }, () => {
      this.onFilterAdded(filter, false);
    });
  };

  reinitializeFilter = (filter) => {
    this.setState({
      filter,
    });
  };

  cleanAll = () => {
    this.setState({
      filter: {},
    }, () => {
      const { filter } = this.state;

      this.onFilterAdded(filter, false);
    });
  };

  cleanFilter = () => {
    this.setState({ filter: [] });
  };

  onFilterAdded = (filter, resetPosition) => {
    const { filterCallback } = this.props;

    if (typeof filterCallback === 'function') {
      filterCallback(filter, resetPosition);
    }
  };

  renderFilterItem = (item, index) => {
    const tmpItem = item;
    tmpItem.description = item.description || item.propertyName;
    tmpItem.type = item || 'text';
    tmpItem.queryOption = item.query != null ? item.query : QueryOptions.STARTS_WITH;
    tmpItem.width = item.width;

    const props = {
      index,
      item: tmpItem,
      autoFocus: item.autoFocus,
      addFilter: this.addFilter,
      range: item.range,
      right: item.right,
      customFilter: item.customFilter,
    };

    return this.renderComponent(props, index);
  };

  renderComponent = (props, index) => {
    switch (props.item.component) {
      case FilterTypes.SELECT:
        return (
          <Filter.Select
            key={index}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
          />
        );

      case FilterTypes.AUTOCOMPLETE:
        return (
          <Filter.Autocomplete
            key={index}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
          />
        );

      case FilterTypes.RANGE:
        return (
          <Filter.Range
            key={index}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
          />
        );

      case FilterTypes.DATEPICKER:
        return (
          <Filter.DatePicker
            key={index}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
          />
        );

      case FilterTypes.DATERANGE:
        return (
          <Filter.DateRange
            key={index}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
          />
        );

      case FilterTypes.INPUT:
      default:
        return (
          <Filter.Input
            key={index}
            ref={(r) => { this.filterRefs[props.item.propertyName] = r; }}
            {...props} // eslint-disable-line react/jsx-props-no-spreading
          />
        );
    }
  };

  toggleOtherFilters = () => {
    this.setState((prevState) => ({ showOtherFilters: !prevState.showOtherFilters }));
  };

  getFilterClasses = (show) => cns(
    'filter__campiRicerca',
    {
      'filter__campiRicerca--show': show,
    },
  );

  render() {
    const { filtriRicerca, show, otherFiltersLabel } = this.props;
    const { filter, showOtherFilters } = this.state;
    const [filtriRicercaMain, filtriRicercaOther] = filtriRicerca.reduce((res, el) => {
      res[el.showInOthers ? 1 : 0].push(el);
      return res;
    }, [[], []]);
    return (
      <div className="filter">
        <div className="filter__separator" />
        <div className="filter__container">
          <div className={this.getFilterClasses(show)}>
            {filtriRicercaMain && filtriRicercaMain.map(this.renderFilterItem)}
          </div>
          {filtriRicercaOther && filtriRicercaOther.length > 0 && (
            <div className="filter__campiRicercaOthers">
              <span
                className="filter__filterOtherToggle"
                onClick={this.toggleOtherFilters}
                role="presentation"
              >
                {showOtherFilters ? <DownIcon /> : <SortRightIcon />}
                {otherFiltersLabel}
              </span>
              <div className={this.getFilterClasses(showOtherFilters)}>
                {filtriRicercaOther.map(this.renderFilterItem)}
              </div>
            </div>
          )}
          <div className="filter__separator" />
          {Object.keys(filter).length > 0
            && (
              <SelectedFilters
                filters={filter}
                onClean={this.cleanAll}
                onRemoveFilter={this.removeFilter}
              />
            )}
          <div className="filter__separator" />
        </div>
      </div>
    );
  }
}

Filter.propTypes = {
  /**
  Funzione di callback che viene invocata ad ogni modifica dei filtri selezionati,
  riceve come parametro un oggetto contenente i dati di filtraggio in cui per
  ogni proprietà abbiamo un insieme di filtri scelti:

  ```
   filterCallback(filter, resetPosition)
  ```

  dove:

  - *filter*: dati di filtraggio in cui per ogni proprietà abbiamo un insieme di filtri scelti
  - *resetPosition*: ritorna true solo durante il caricamento del componente
  */
  filterCallback: PropTypes.func,
  /**
Array contenente una lista di oggetti con la seguente struttura:

- `propertyName` (required): nome del campo da filtrare, ossia il nome della proprietà lato server
- `description`: testo visualizzato nella label del campo di ricerca.
                 Se non impostato, verrà utilizzato il `propertyName`
- `customFilter`: con questo parametro è possibile passare una funzione per forzare il comportamento del filtro.   
                  Ad esempio `customFilter: `(a, b) => `(${a} in [${b}])` `;   
                  quando dovrà essere passato il filtro come parametro per una chiamata verrà usata questa funzione per formattarlo.
- `type`: tipo dell'input (es text, number)
- `query`: enum rappresentante la query option.   
           Per utilizzarlo è necessario importare l'oggetto `QueryOptions` mediante il seguente import:   
           ``import {QueryOptions} from "framework-ui-components"``.   
           Se la proprietà non viene indicata sarà utilizzato  il valore di default `QueryOptions.STARTS_WITH`.
- `component`: tipo del filtro;
               i tipi utilizzabili sono disponibili importando l'enum `FilterTypes`   
               (``import {FilterTypes} from "framework-ui-components"``).   
               Se non viene passata questa proprietà, il valore di default sarà `INPUT`.   
              Può assumere i seguenti valori:

  - `INPUT`
  - `SELECT`
  - `AUTOCOMPLETE`: _nota bene_ : l'oggetto ritornato dal controller dovrà essere un array di oggetti con proprietà `Value` e `Description`.
  - `RANGE`
  - `DATEPICKER`
- `options`: disponibili solo per il componente `SELECT`,
             definisce le options da visualizare tramite un array di oggetti, contenenti le proprietà `Description` e `Value`.   
             Es: ``options: [{Description: 'M', Value: 1},{Description: 'F', Value: 2}]``
- `autoFocus`: indica se il filtro di default deve ricevere il focus (non funziona se impostato `showInOthers` a true)
- `range`: permette di definire dei limiti sul valore da inserire come filtro.   
           Ad esempio ``range: { to: new Date() }`` in un filtro `DATEPICKER` indicherà che non
          potranno essere selezionate date superiori all'oggi
- `url`: disponibile solo per il componente `AUTOCOMPLETE`, definisce l'url dell'endpoint attraverso cui effettuare le ricerche
- `renderProperty`: Nel caso di filtro `AUTOCOMPLETE` permette di indicare il nome della proprietà da renderizzare nelle opzioni selezionabili
- `width`: permette di poter determinare la larghezza che il filtro dovrà occupare nel content dei filtri (px, ch, em, %)
- `showInOthers`: permette di spostare il filtro in una sezione "Altri filtri" inizialmente non visualizzata
  */
  filtriRicerca: PropTypes.arrayOf(PropTypes.shape({
    propertyName: PropTypes.string.isRequired,
    description: PropTypes.string,
    customFilter: PropTypes.func,
    type: PropTypes.string,
    query: PropTypes.number,
    component: PropTypes.number,
    options: PropTypes.array,
    autoFocus: PropTypes.bool,
    range: PropTypes.object,
    url: PropTypes.string,
    renderProperty: PropTypes.string,
    width: PropTypes.string,
    showInOthers: PropTypes.bool,
  })),
  /**
Array contenente una lista di oggetti che verranno utilizzati
per settare dei filtri iniziali:

- `propertyName`: nome del campo da filtrare, ossia il nome della proprietà lato server
- `description`: testo visualizzato nella label del campo di ricerca.
                 Se non impostato, verrà utilizzato il `propertyName`
- `displayValue`: testo da mostrare nel caso in cui si voglia sovrascrivere il valore di default
- `query`: enum rappresentante la query option.
           Per utilizzarlo è necessario importare l'oggetto `QueryOptions` mediante il seguente import:
           ``import {QueryOptions} from "framework-ui-components"``.
           Se la proprietà non viene indicata sarà utilizzato il valore di default `QueryOptions.STARTS_WITH`.
- `value`: valore iniziale del filtro
   */
  initialFilters: PropTypes.arrayOf(PropTypes.shape({
    propertyName: PropTypes.string.isRequired,
    description: PropTypes.string,
    displayValue: PropTypes.string,
    query: PropTypes.number,
    value: PropTypes.any,
  })),
  /** Indica se il componente è espanso e i filtri visibili */
  show: PropTypes.bool,
  /**
   * Indica l'etichetta da visualizzare per la sezione *"Altri filtri"* (valore di default).
   * Per spostare un filtro in questa sezione basta impostare a true il parametro `showInOthers`
   * nei filtri di ricerca
   */
  otherFiltersLabel: PropTypes.string,
};

Filter.defaultProps = {
  filterCallback: null,
  filtriRicerca: [],
  initialFilters: [],
  show: false,
  otherFiltersLabel: 'Altri filtri',
};

Filter.Input = InputFilter;
Filter.Select = SelectFilter;
Filter.Autocomplete = AutocompleteFilter;
Filter.Range = InputRangeFilter;
Filter.DatePicker = DatePickerFilter;
Filter.FilterTypes = FilterTypes;
Filter.QueryOptions = QueryOptions;
Filter.DateRange = DateRangeFilter;

export default Filter;
